import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  BarChart3,
  BellRing,
  BookOpen,
  Bot,
  ClipboardCheck,
  Database,
  FileSpreadsheet,
  Gauge,
  GitBranch,
  KeyRound,
  Layers,
  Lock,
  MessageSquare,
  Plug,
  ShieldCheck,
  Store,
  Target,
  UploadCloud,
  Users
} from "lucide-react";
import "./styles.css";
import { ApiError, apiGet, apiPost } from "./api";
import {
  buildImpactReview,
  buildKnowledgeItems,
  evaluateAdjustmentGuardrail,
  type AdActionEvidence
} from "./domain/adReviewEngine";

type Section =
  | "dashboard"
  | "upload"
  | "analysis"
  | "plans"
  | "review"
  | "traffic"
  | "bi"
  | "library"
  | "competitors"
  | "models"
  | "users"
  | "connectors";

const navItems: Array<{ id: Section; label: string; icon: React.ElementType }> = [
  { id: "dashboard", label: "首页总控", icon: Gauge },
  { id: "upload", label: "报表上传", icon: UploadCloud },
  { id: "analysis", label: "分析任务", icon: Activity },
  { id: "plans", label: "行动计划", icon: Target },
  { id: "review", label: "复盘审计", icon: ClipboardCheck },
  { id: "traffic", label: "流量架构", icon: GitBranch },
  { id: "bi", label: "BI看板", icon: BarChart3 },
  { id: "library", label: "词库/CPC库", icon: Database },
  { id: "competitors", label: "竞品库", icon: Layers },
  { id: "models", label: "模型API", icon: Bot },
  { id: "users", label: "用户权限", icon: Users },
  { id: "connectors", label: "外部接口", icon: Plug }
];

const ActionContext = React.createContext<(message: string, navigateTo?: Section) => void>(() => undefined);

function useAction() {
  return React.useContext(ActionContext);
}

const dimensions = [
  "预算调整",
  "竞价调整",
  "加词",
  "否词/否ASIN",
  "拉精准",
  "分流隔离",
  "广告位溢价",
  "企业购",
  "受众/AMC",
  "SP/SB/SD结构",
  "SBV素材",
  "前5秒切片",
  "Rufus语义词",
  "硬参数词卡位"
];

type LlmProvider = {
  id: string;
  name: string;
  type: string;
  baseUrl: string;
  model: string;
  status: string;
  quota: string;
  apiKey?: string;
  hasApiKey?: boolean;
  enabled?: boolean;
};

type ManagedUser = {
  id: string;
  username: string;
  displayName: string;
  role: "admin" | "operator" | "auditor";
  status: "active" | "disabled";
  maxDevices: number;
  activeDeviceCount: number;
  permissions: string[];
  monthlyTokenLimit: number;
  monthlyCostLimitUsd: number;
  usedTokens: number;
  usedCostUsd: number;
  password?: string;
};

type SessionUser = {
  id: string;
  username: string;
  display_name?: string;
  displayName?: string;
  role: "admin" | "operator" | "auditor";
  permissions: string[];
  max_devices?: number;
  maxDevices?: number;
};

type ManagedDevice = {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  deviceId: string;
  deviceLabel: string;
  platform: string;
  status: "active" | "released";
  firstSeenAt: string;
  lastSeenAt: string;
};

type CloudOverview = {
  kpis: Array<{ label: string; value: string; delta: string }>;
  cloudTables: string[][];
  adminScopes: string[][];
  recentSyncEvents: Array<{
    id: string;
    actor: string;
    sourceDevice: string;
    entityType: string;
    action: string;
    createdAt: string;
  }>;
};

const initialLlmProviders: LlmProvider[] = [
  { id: "deepseek", name: "DeepSeek", type: "OpenAI兼容", baseUrl: "https://api.deepseek.com/v1", model: "deepseek-chat", status: "未填Key", quota: "$100/月" },
  { id: "qwen", name: "通义千问", type: "OpenAI兼容", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1", model: "qwen-max", status: "未填Key", quota: "$100/月" },
  { id: "openai-proxy", name: "OpenAI中转", type: "Proxy", baseUrl: "自定义", model: "gpt-5.5", status: "未填Key", quota: "$300/月" },
  { id: "gemini", name: "Gemini官方", type: "Gemini", baseUrl: "官方接口", model: "gemini-3.1-pro", status: "未填Key", quota: "$200/月" },
  { id: "claude", name: "Claude官方", type: "Anthropic", baseUrl: "官方接口", model: "claude-4.8", status: "未填Key", quota: "$200/月" }
];

const actionPlans = [
  {
    priority: "P0",
    dimension: "预算调整",
    asin: "B0813TDBPT",
    campaign: "B0813TDBPT-SP-Exact-Harvest-v1",
    adGroup: "90w usb c charger",
    target: "90w usb c charger",
    current: "近3天 ACOS 18.6%，Orders 6，Spend $42.8",
    action: "预算 +100%，保留固定竞价",
    owner: "运营A",
    due: "今日"
  },
  {
    priority: "P0",
    dimension: "否词/否ASIN",
    asin: "B07VPPWNNR",
    campaign: "B07VPPWNNR-SP-Broad-Prospecting-v1",
    adGroup: "dell laptop charger",
    target: "cheap charger cable",
    current: "Clicks 12，Orders 0，Spend $18.4",
    action: "加入否定精准",
    owner: "运营B",
    due: "今日"
  },
  {
    priority: "P1",
    dimension: "广告位溢价",
    asin: "B0833WSJN8",
    campaign: "B0833WSJN8-SP-PT-Competitor-v1",
    adGroup: "LG monitor adapter",
    target: "B0COMPETITOR1",
    current: "Product Pages ACOS 22.4%，CVR 18.1%",
    action: "商品页溢价 +20%",
    owner: "运营A",
    due: "周三"
  },
  {
    priority: "P1",
    dimension: "Rufus语义词",
    asin: "B0886KTSKT",
    campaign: "B0886KTSKT-SP-Broad-Prospecting-v2",
    adGroup: "sony tv power supply",
    target: "replacement power adapter for sony bravia",
    current: "搜索词有转化，ABA缺失",
    action: "低竞价语义广泛测试 $0.35",
    owner: "运营C",
    due: "本周"
  }
];

const keywordTierRows = [
  ["核心词", "90w usb c charger / lenovo thinkpad charger", "搜索首页 Top 1-8 / Top of Search", "$2.60-$4.20", "$35-$80", "SP Exact Harvest + SB Defense", "利润收割与排名主战场"],
  ["1级大词", "laptop charger usb c / usb c laptop charger", "首页中上段 / Top低占比", "$1.80-$3.20", "$20-$55", "SP Phrase/Broad Prospecting", "规模拓量，严格控ACOS"],
  ["2级大词", "90 watt usb c charger / computer charger usb c", "首页或第二页首屏", "$1.20-$2.60", "$12-$35", "SP Phrase + Spec Campaign", "承接参数与兼容流量"],
  ["1级长尾词", "for thinkpad x1 carbon 90w charger", "首页自然/广告混合位", "$0.80-$1.80", "$8-$24", "SP Exact/Phrase Long-tail", "新品冷启动低成本出单"],
  ["2级长尾词", "replacement charger for lenovo yoga 7i", "首页尾部或第二页", "$0.35-$1.20", "$5-$15", "SP Broad Semantic + Auto Close", "低CPC测试，沉淀词库"]
];

const lifecycleRows = [
  ["新品0-14天", "验证词路", "销售额15%-25%", "长尾/参数词优先，核心词小预算卡位", "ACOS可短期高于目标15pp"],
  ["爬坡15-45天", "放大有效词", "销售额12%-18%", "核心词+1级长尾提高Top位", "3天无单词降价或否定"],
  ["稳定45天+", "控TACOS提利润", "销售额8%-12%", "Exact收割，Broad控量挖词", "TACOS超长期目标即降载"],
  ["大促/旺季", "抢排名和份额", "销售额18%-30%", "核心词、竞品ASIN、SBV同步放量", "小时级预算监控"]
];

const launchRows = [
  ["SP-Auto-Close", "挖真实搜索词", "15%", "低CPC，3天看词", "无单高点击词转否词候选"],
  ["SP-Exact-Harvest", "核心/长尾收割", "30%", "一个关键词一个广告组", "低ACOS词加预算"],
  ["SP-Phrase-Broad-Prospecting", "拓展1/2级词", "25%", "精准词反向否定防内耗", "每周沉淀新词"],
  ["SP-PT-Competitor", "竞品ASIN截流", "15%", "价格/评分有优势再加预算", "差评竞品优先"],
  ["SB/SBV-Brand-Defense", "品牌防守和视频教育", "10%", "兼容、规格、安全证据", "前5秒痛点切片"],
  ["SD-Remarketing", "召回和闭环", "5%", "加购/浏览再营销", "老客不在SP里误否"]
];

const asinPerformanceRows = [
  ["B0813TDBPT", "Lenovo 90W USB-C", "US", "$16,614", "983", "$2,552", "49.2%", "15.4%", "P0 控ACOS", "Exact预算重分配"],
  ["B07VPPWNNR", "Dell 65W", "US", "$7,150", "488", "$668", "32.7%", "9.3%", "P1 放长尾", "长尾型号词扩量"],
  ["B0833WSJN8", "LG Monitor", "US", "$1,687", "115", "$276", "22.8%", "16.4%", "P1 控页面", "Listing承接复盘"],
  ["B0886KTSKT", "Sony Bravia", "US", "$1,135", "84", "$193", "23.4%", "17.0%", "P1 竞品截流", "商品页预算回收"]
];

const trafficContributionRows = [
  ["核心词", "$7,860", "36%", "28.4%", "$2.88", "Top 12%", "提高Top of Search占比"],
  ["1级大词", "$4,240", "19%", "44.8%", "$2.31", "首页中段", "控预算，低效词降价"],
  ["2级大词", "$3,780", "17%", "31.6%", "$1.82", "首页/第二页", "保留参数词"],
  ["1级长尾词", "$3,110", "14%", "21.2%", "$1.12", "首页尾部", "拉精准"],
  ["2级长尾词", "$1,940", "9%", "18.9%", "$0.62", "第二页", "低CPC挖词"],
  ["竞品ASIN", "$1,120", "5%", "26.7%", "$1.45", "商品页", "只打有优势竞品"]
];

const alertsRows = [
  ["高", "B0813TDBPT", "TACOS 15.4% 高于长期目标12%", "削减1级大词预算，Exact保留"],
  ["高", "B0813TDBPT", "近7天ACOS从37.4%升至49.2%", "先否词/降价，再看页面转化"],
  ["中", "B0833WSJN8", "业务CVR 3.49%，页面承接偏弱", "页面与报价优先，不激进放量"],
  ["中", "B0886KTSKT", "商品页投放产出窄", "回收商品页预算到型号词/竞品池"]
];

const budgetPacingRows = [
  ["US主店", "$92", "$131", "142%", "超速", "今晚先限低效Broad"],
  ["EU店", "$38", "$31", "82%", "正常", "保留预算节奏"],
  ["JP店", "$24", "$15", "62%", "偏慢", "补充长尾词和竞品ASIN"]
];

const actionReviewRows = [
  ["否词", "91", "已执行64", "$183", "B0813TDBPT大词浪费下降"],
  ["预算调整", "28", "已执行20", "$412", "Exact收割预算占比提升"],
  ["竞价调整", "146", "已执行112", "ACOS -4.2pp", "高ACOS词降价有效"],
  ["拉精准", "37", "已执行18", "Orders +31", "长尾型号词表现最好"]
];

const reviewActions: AdActionEvidence[] = [
  {
    id: "act-20260601-001",
    asin: "B0813TDBPT",
    campaignName: "B0813TDBPT-SP-Exact-Harvest-v1",
    adGroupName: "90w usb c charger",
    targetText: "90w usb c charger",
    actionType: "decrease_budget",
    keywordTier: "核心词",
    changePercent: -30,
    beforeValue: "$80/day",
    afterValue: "$56/day",
    recent30dOrders: 36,
    historicalOrders: 160,
    recent30dSpend: 420,
    historicalPeakOrders: 46,
    outcomeOrdersDelta: -19,
    outcomeAcosDelta: -2.4
  },
  {
    id: "act-20260602-004",
    asin: "B07VPPWNNR",
    campaignName: "B07VPPWNNR-SP-Exact-Longtail-v1",
    adGroupName: "dell laptop charger",
    targetText: "for dell latitude 5420 charger",
    actionType: "increase_bid",
    keywordTier: "1级长尾词",
    changePercent: 15,
    beforeValue: "$0.95",
    afterValue: "$1.09",
    recent30dOrders: 8,
    historicalOrders: 12,
    outcomeOrdersDelta: 11,
    outcomeAcosDelta: -4.1
  },
  {
    id: "act-20260603-007",
    asin: "B0833WSJN8",
    campaignName: "B0833WSJN8-SP-Broad-Prospecting-v1",
    adGroupName: "LG monitor adapter",
    targetText: "cheap universal adapter",
    actionType: "add_negative",
    keywordTier: "2级大词",
    changePercent: 0,
    beforeValue: "active",
    afterValue: "negative exact",
    recent30dOrders: 0,
    historicalOrders: 0,
    outcomeOrdersDelta: 2,
    outcomeAcosDelta: -7.3
  },
  {
    id: "act-20260604-011",
    asin: "B0813TDBPT",
    campaignName: "B0813TDBPT-SP-Phrase-Prospecting-v2",
    adGroupName: "usb c laptop charger",
    targetText: "laptop charger usb c",
    actionType: "decrease_bid",
    keywordTier: "1级大词",
    changePercent: -18,
    beforeValue: "$2.20",
    afterValue: "$1.80",
    recent30dOrders: 18,
    historicalOrders: 74,
    outcomeOrdersDelta: -7,
    outcomeAcosDelta: -1.1
  }
];

const impactReview = buildImpactReview({
  previousOrders: 118,
  currentOrders: 84,
  previousSales: 6820,
  currentSales: 4910,
  actions: reviewActions
});
const reviewKnowledgeItems = buildKnowledgeItems(reviewActions);

const connectorRows = [
  ["ERP", "领星", "预留", "销售/广告同步、Bulk状态"],
  ["ERP", "赛狐", "预留", "销售/广告同步、Listing数据"],
  ["ERP", "优麦云", "预留", "广告执行桥接、ERP汇总"],
  ["MCP", "卖家精灵", "预留", "关键词、竞品、类目数据"],
  ["MCP", "SIF", "预留", "排名、Listing、语义词"],
  ["MCP", "Sorftime", "预留", "市场排名、流量研究"],
  ["Amazon", "Ads API", "审批后启用", "Campaign读取、Bulk、执行草稿"],
  ["Amazon", "SP-API / AI / AMC", "预留", "业务报告、Audience、AMC查询"],
  ["Market", "Keepa", "预留", "价格、BSR、Buy Box、竞品历史"],
  ["协作", "钉钉", "预留", "任务推送、审批通知、周报机器人"],
  ["协作", "飞书", "预留", "任务推送、审批通知、周报/文档同步"]
];

function App() {
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => {
    const stored = localStorage.getItem("ad-agent-user");
    if (!stored || !localStorage.getItem("ad-agent-token")) return null;
    try {
      return JSON.parse(stored) as SessionUser;
    } catch {
      return null;
    }
  });
  const [active, setActive] = useState<Section>("dashboard");
  const [toast, setToast] = useState("");
  const allowedNavItems = useMemo(() => {
    if (!sessionUser) return [];
    if (sessionUser.permissions.includes("all")) return navItems;
    return navItems.filter((item) => item.id === "dashboard" || sessionUser.permissions.includes(item.id));
  }, [sessionUser]);

  function handleAction(message: string, navigateTo?: Section) {
    if (navigateTo) setActive(navigateTo);
    setToast(message);
    window.setTimeout(() => setToast(""), 1800);
  }

  if (!sessionUser) return <Login onLogin={(user) => setSessionUser(user)} />;

  return (
    <ActionContext.Provider value={handleAction}>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="brand">
            <div className="brand-mark">AA</div>
            <div>
              <strong>Ad Agent</strong>
              <span>Amazon 广告中枢</span>
            </div>
          </div>
          <nav>
            {allowedNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.id} className={active === item.id ? "nav-item active" : "nav-item"} onClick={() => {
                  setToast("");
                  setActive(item.id);
                }}>
                  <Icon size={18} />
                  {item.label}
                </button>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            <ShieldCheck size={18} />
            <span>人工确认优先，API自动执行默认关闭</span>
          </div>
        </aside>
        <main>
          <Topbar active={active} user={sessionUser} />
          {active === "dashboard" && <Dashboard />}
          {active === "upload" && <UploadFlow />}
          {active === "analysis" && <Analysis />}
          {active === "plans" && <ActionPlans />}
          {active === "review" && <ReviewAudit />}
          {active === "traffic" && <TrafficArchitecture />}
          {active === "bi" && <Bi />}
          {active === "library" && <Library />}
          {active === "competitors" && <Competitors />}
          {active === "models" && <Models />}
          {active === "users" && <UsersPage />}
          {active === "connectors" && <Connectors />}
        </main>
        {toast && <div className="toast">{toast}</div>}
      </div>
    </ActionContext.Provider>
  );
}

function Login({ onLogin }: { onLogin: (user: SessionUser) => void }) {
  const [username, setUsername] = useState("yangle");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("请输入账号和密码。");
      return;
    }

    setLoading(true);
    try {
      const result = await apiPost<{ token: string; user: SessionUser }>("/api/auth/login", {
        username,
        password,
        ...getDeviceIdentity()
      });
      localStorage.setItem("ad-agent-token", result.token);
      localStorage.setItem("ad-agent-user", JSON.stringify(result.user));
      onLogin(result.user);
    } catch (loginError) {
      const isLocalDemo = ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname);
      if (isLocalDemo && !(loginError instanceof ApiError) && username === "yangle") {
        const demoUser: SessionUser = {
          id: "user_admin_yangle",
          username: "yangle",
          displayName: "Yangle Admin",
          role: "admin",
          permissions: ["all"]
        };
        localStorage.setItem("ad-agent-user", JSON.stringify(demoUser));
        onLogin(demoUser);
        return;
      }
      setError(loginError instanceof Error ? loginError.message : "登录失败，请检查账号和密码。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <form className="login-panel" onSubmit={handleLogin}>
        <div className="brand large">
          <div className="brand-mark">AA</div>
          <div>
            <strong>Amazon Ad Agent</strong>
            <span>广告分析与执行计划 SaaS</span>
          </div>
        </div>
        <label>
          管理员账号
          <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
        </label>
        <label>
          密码
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="primary" type="submit" disabled={loading}>
          <Lock size={18} />
          {loading ? "登录中" : "登录并进入工作台"}
        </button>
        <p>生产环境使用哈希密码和短期登录 token，本机开发可进入演示模式。</p>
      </form>
    </div>
  );
}

function getDeviceIdentity() {
  let deviceId = localStorage.getItem("ad-agent-device-id");
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem("ad-agent-device-id", deviceId);
  }
  return {
    deviceId,
    deviceLabel: navigator.platform || "Desktop",
    platform: navigator.userAgent
  };
}

function Topbar({ active, user }: { active: Section; user: SessionUser }) {
  const act = useAction();
  const config = getPageHeader(active);
  return (
    <header className="topbar">
      <div>
        <h1>{config.title}</h1>
        <p>{config.subtitle}</p>
      </div>
      <div className="top-user">
        <strong>{user.display_name ?? user.displayName ?? user.username}</strong>
        <span>{roleLabel(user.role)} · {getDeviceIdentity().deviceLabel}</span>
      </div>
      {config.showActions && (
        <div className="top-actions">
          <button onClick={() => exportCsv()}>导出 Excel</button>
          <button className="primary" onClick={() => act("已进入报表上传向导，可以开始创建新分析。", "upload")}>新建分析</button>
        </div>
      )}
    </header>
  );
}

function getPageHeader(active: Section) {
  const headers: Record<Section, { title: string; subtitle: string; showActions: boolean }> = {
    dashboard: { title: "首页总控", subtitle: "查看广告经营、上传流程、今日动作和系统接口状态。", showActions: true },
    upload: { title: "报表上传", subtitle: "上传广告、销售、ABA、ERP表格，并检查缺失字段。", showActions: false },
    analysis: { title: "分析任务", subtitle: "管理报表解析、规则引擎和LLM策略生成任务。", showActions: true },
    plans: { title: "行动计划", subtitle: "按ASIN、Campaign、AdGroup、Target对齐预算、竞价、否词和加词动作。", showActions: true },
    review: { title: "复盘审计", subtitle: "按调整时间窗复盘所有广告动作、Bulk执行结果、单量涨跌原因和知识库沉淀。", showActions: false },
    traffic: { title: "流量架构", subtitle: "沉淀系列词层级、CPC位置、生命周期预算和新品广告模板。", showActions: false },
    bi: { title: "BI经营驾驶舱", subtitle: "按店铺、站点、系列和ASIN复盘销售、花费、ACOS、TACOS和行动结果。", showActions: true },
    library: { title: "词库 / CPC库", subtitle: "维护每个系列、站点、词层级和目标位置的关键词资产。", showActions: false },
    competitors: { title: "竞品库", subtitle: "维护系列竞品ASIN、品牌、价格、评分、BSR和Buy Box变化。", showActions: false },
    models: { title: "模型 API 配置", subtitle: "只用于新增和维护 LLM 供应商、Base URL、模型名、Key状态和额度。", showActions: false },
    users: { title: "用户权限", subtitle: "管理运营账号、功能权限、API使用量和金额额度。", showActions: false },
    connectors: { title: "外部接口配置", subtitle: "只用于预留和维护 ERP、MCP、Amazon、Keepa、钉钉、飞书等 API / Skills 接入。", showActions: false }
  };
  return headers[active];
}

function Dashboard() {
  const [overview, setOverview] = useState<CloudOverview | null>(null);
  const [cloudError, setCloudError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ overview: CloudOverview }>("/api/cloud/overview")
      .then((payload) => {
        setOverview(payload.overview);
        setCloudError(null);
      })
      .catch((error) => {
        setCloudError(error instanceof Error ? error.message : "云端总览暂时不可用");
      });
  }, []);

  return (
    <div className="content-grid">
      <KpiRow kpis={overview?.kpis} />
      <section className="panel full">
        <PanelTitle icon={Database} title="云端数据互通" action="刷新云端" actionMessage="云端总览已刷新，所有桌面端和网页端共用同一个云数据库。" />
        {cloudError && <div className="notice warning">未登录管理员或云端暂时不可用，当前展示本地兜底数据：{cloudError}</div>}
        <DataTable
          columns={["数据域", "云端记录", "管理员可管理"]}
          rows={overview?.cloudTables ?? [
            ["团队账号", "等待登录后读取", "账号、密码、角色、权限、设备数、API额度"],
            ["上传报表", "等待上传后沉淀", "店铺、站点、周期、ACOS/TACOS目标、缺表清单"],
            ["分析与行动", "等待生成后沉淀", "ASIN、Campaign、AdGroup、Target维度复盘"],
            ["外部接口", "预留入口", "ERP、MCP、Amazon、Keepa、钉钉、飞书"]
          ]}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={ShieldCheck} title="管理员数据权限" />
        <DataTable
          columns={["范围", "说明"]}
          rows={overview?.adminScopes ?? [
            ["数据归属", "所有团队电脑和网页端统一上传到云数据库"],
            ["管理员控制", "管理员统一管理账号、设备、额度、接口和操作审计"],
            ["复盘链路", "后续可按ASIN/广告活动/广告组追溯所有动作"]
          ]}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={Activity} title="最近云同步" />
        <DataTable
          columns={["时间", "账号", "设备", "对象", "动作"]}
          rows={(overview?.recentSyncEvents.length ? overview.recentSyncEvents : [
            { id: "empty", createdAt: "-", actor: "暂无", sourceDevice: "等待团队使用", entityType: "cloud", action: "ready" }
          ]).map((event) => [
            event.createdAt,
            event.actor,
            event.sourceDevice,
            event.entityType,
            event.action
          ])}
        />
      </section>
      <section className="panel wide">
        <PanelTitle icon={FileSpreadsheet} title="三步工作流" action="查看缺失清单" actionMessage="已跳转到报表上传页查看缺失清单。" navigateTo="upload" />
        <div className="steps">
          {["运营上传广告/销售/市场表格", "填写15天与长期ACOS/TACOS目标", "输出14维行动计划与一周任务"].map((step, index) => (
            <div className="step" key={step}>
              <span>{index + 1}</span>
              <strong>{step}</strong>
              <p>{index === 0 ? "SP/SB/SD、Business Reports、ABA、ERP汇总" : index === 1 ? "短期容忍值、长期目标、预算护栏" : "人工确认、Bulk导出、API执行草稿"}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="panel">
        <PanelTitle icon={Target} title="今日优先动作" />
        <TaskList />
      </section>
      <section className="panel">
        <PanelTitle icon={Plug} title="接口状态" />
        <StatusList />
      </section>
      <section className="panel full">
        <PanelTitle icon={GitBranch} title="垂直流量架构总览" action="进入流量架构" actionMessage="已打开系列流量架构：按词层级、CPC位置和生命周期预算复用。" navigateTo="traffic" />
        <DataTable
          columns={["层级", "目标位置", "建议CPC", "日预算", "广告结构", "用途"]}
          rows={keywordTierRows.map(([tier, , position, cpc, budget, structure, role]) => [tier, position, cpc, budget, structure, role])}
        />
      </section>
    </div>
  );
}

function KpiRow({ kpis }: { kpis?: CloudOverview["kpis"] }) {
  const displayKpis = kpis?.length ? kpis.map((item) => [item.label, item.value, item.delta]) : [
    ["30天销售额", "$26,589", "+8.4%"],
    ["广告花费", "$3,691", "-3.1%"],
    ["ACOS", "31.8%", "-4.2%"],
    ["TACOS", "13.9%", "-1.6%"]
  ];
  return (
    <section className="kpi-row">
      {displayKpis.map(([label, value, delta]) => (
        <div className="kpi" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
          <em>{delta}</em>
        </div>
      ))}
    </section>
  );
}

function UploadFlow() {
  const [files, setFiles] = useState(["SP Search Term.xlsx", "Campaign Summary.csv", "Business Reports.xlsx", "ABA Search Query.xlsx"]);
  const missing = useMemo(() => ["Placement 报表", "Audience/AMC 报表", "SBV 视频数据"], []);
  const act = useAction();
  return (
    <div className="content-grid">
      <section className="panel wide">
        <PanelTitle icon={UploadCloud} title="报表上传向导" action="识别字段" actionMessage="字段识别已完成：4个文件可解析，3类报表待补充。" />
        <div className="upload-zone">
          <UploadCloud size={32} />
          <strong>拖入亚马逊后台、ERP、ABA、商机探测器表格</strong>
          <span>第一版支持 Excel/CSV；ERP API、Amazon API 和 MCP 同步入口已预留。</span>
        </div>
        <div className="file-list">
          {files.map((file) => (
            <div className="file-row" key={file}>
              <FileSpreadsheet size={17} />
              <span>{file}</span>
              <em>已识别</em>
            </div>
          ))}
        </div>
        <button onClick={() => {
          setFiles([...files, "Placement Report.xlsx"]);
          act("已补充 Placement Report，广告位建议可生成。");
        }}>模拟补充 Placement</button>
      </section>
      <section className="panel">
        <PanelTitle icon={Target} title="目标参数" />
        <div className="form-grid">
          <label>15天 ACOS 容忍<input defaultValue="35%" /></label>
          <label>15天 TACOS 容忍<input defaultValue="15%" /></label>
          <label>长期 ACOS 目标<input defaultValue="25%" /></label>
          <label>长期 TACOS 目标<input defaultValue="12%" /></label>
          <label>预算护栏<input defaultValue="销售额 10%" /></label>
          <label>站点<input defaultValue="US / EU / JP" /></label>
        </div>
      </section>
      <section className="panel">
        <PanelTitle icon={ShieldCheck} title="缺失清单" />
        <ul className="clean-list">
          {missing.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Analysis() {
  return (
    <section className="panel full">
      <PanelTitle icon={Activity} title="分析任务队列" action="创建任务" actionMessage="已创建分析任务草稿，等待上传报表确认。" navigateTo="upload" />
      <DataTable
        columns={["任务", "店铺/站点", "ASIN数", "状态", "模型", "费用", "结果"]}
        rows={[
          ["2026-06-06 第1波数据", "US 主店", "4", "已完成", "GPT/Claude中转", "$1.82", "生成行动计划"],
          ["新品系列 CPC 库同步", "JP 店铺", "12", "排队", "规则引擎", "$0.00", "等待解析"],
          ["Keepa竞品历史", "EU 店铺", "28", "预留", "Keepa API", "$0.00", "未启用"]
        ]}
      />
    </section>
  );
}

function ActionPlans() {
  return (
    <section className="panel full">
      <PanelTitle icon={Target} title="14维行动计划" action="生成Bulk草稿" actionMessage="Bulk Operation 草稿已生成，已进入复盘审计链路。" navigateTo="review" />
      <div className="dimension-strip">
        {dimensions.map((dimension) => (
          <span key={dimension}>{dimension}</span>
        ))}
      </div>
      <div className="plan-table">
        <DataTable
          columns={["优先级", "维度", "ASIN", "Campaign", "AdGroup", "Target", "当前数据", "建议动作", "负责人", "执行时间"]}
          rows={actionPlans.map((plan) => [
            plan.priority,
            plan.dimension,
            plan.asin,
            plan.campaign,
            plan.adGroup,
            plan.target,
            plan.current,
            plan.action,
            plan.owner,
            plan.due
          ])}
        />
      </div>
    </section>
  );
}

function ReviewAudit() {
  const act = useAction();
  async function syncReviewActions() {
    try {
      await Promise.all(reviewActions.map((action) => apiPost("/api/ad-actions", { ...action, status: "draft" })));
      act("Bulk/API动作已同步到云数据库，管理员可按ASIN、广告活动、广告组和Target复盘。");
    } catch (error) {
      act(error instanceof Error ? error.message : "广告动作同步失败。");
    }
  }
  const guardrailRows = reviewActions.map((action) => {
    const guardrail = evaluateAdjustmentGuardrail(action);
    return [
      action.asin ?? "-",
      action.campaignName ?? "-",
      action.adGroupName ?? "-",
      action.targetText,
      `${reviewActionLabel(action.actionType)} ${action.changePercent}%`,
      `${action.recent30dOrders}单 / 历史${action.historicalOrders}单`,
      guardrail.riskLevel === "high" ? "高风险" : guardrail.riskLevel === "medium" ? "中风险" : "低风险",
      guardrail.status === "needs_human_review" ? "运营审核 + 管理员知情" : guardrail.requiresOperatorReview ? "运营审核" : "可进入草稿",
      guardrail.reason
    ];
  });

  return (
    <div className="content-grid">
      <section className="panel full review-hero">
        <PanelTitle icon={ClipboardCheck} title="本次复盘窗口：上一次调整后 → 当前" action="生成复盘报告" actionMessage="已生成复盘报告草稿，并把有效动作写入管理员知识库候选。" />
        <div className="review-hero-grid">
          <div>
            <span>时间窗</span>
            <strong>2026-06-01 10:20 → 2026-06-08 09:00</strong>
            <em>US主店 / 3C电源线 / 全ASIN</em>
          </div>
          <div>
            <span>单量变化</span>
            <strong className={impactReview.direction === "decline" ? "danger-text" : "ok-text"}>{impactReview.orderDelta} 单</strong>
            <em>{impactReview.orderDeltaPct.toFixed(1)}% 环比</em>
          </div>
          <div>
            <span>销售额变化</span>
            <strong className={impactReview.salesDelta < 0 ? "danger-text" : "ok-text"}>${impactReview.salesDelta}</strong>
            <em>{impactReview.salesDeltaPct.toFixed(1)}% 环比</em>
          </div>
          <div>
            <span>主结论</span>
            <strong>{impactReview.direction === "decline" ? "下滑需追责复核" : "增长动作可复用"}</strong>
            <em>{impactReview.summary}</em>
          </div>
        </div>
      </section>

      <section className="panel">
        <PanelTitle icon={BellRing} title="下滑主因" action="询问运营" actionMessage="已向运营A发起追问：核心词预算为何下调30%，是否恢复预算？" />
        <div className="evidence-list">
          {impactReview.primaryDrivers.map((action) => (
            <div className="evidence-item danger" key={action.id}>
              <strong>{action.asin} · {action.targetText}</strong>
              <span>{reviewActionLabel(action.actionType)} {action.beforeValue} → {action.afterValue}</span>
              <em>结果：Orders {action.outcomeOrdersDelta}，ACOS {action.outcomeAcosDelta}pp</em>
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <PanelTitle icon={BookOpen} title="上涨动作" action="沉淀知识库" actionMessage="有效动作已进入管理员知识库候选，后续同系列新品可复用。" />
        <div className="evidence-list">
          {impactReview.positiveSignals.map((action) => (
            <div className="evidence-item ok" key={action.id}>
              <strong>{action.asin} · {action.targetText}</strong>
              <span>{reviewActionLabel(action.actionType)} {action.beforeValue} → {action.afterValue}</span>
              <em>结果：Orders +{action.outcomeOrdersDelta}，ACOS {action.outcomeAcosDelta}pp</em>
            </div>
          ))}
        </div>
      </section>

      <section className="panel full">
        <PanelTitle icon={Target} title="Bulk Operation / API 动作审计表" action="同步到云端" onAction={syncReviewActions} />
        <DataTable
          columns={["动作ID", "ASIN", "Campaign", "AdGroup", "Target", "动作", "前值", "后值", "近30天/历史", "结果", "复盘状态"]}
          rows={reviewActions.map((action) => {
            const guardrail = evaluateAdjustmentGuardrail(action);
            return [
              action.id ?? "-",
              action.asin ?? "-",
              action.campaignName ?? "-",
              action.adGroupName ?? "-",
              action.targetText,
              `${reviewActionLabel(action.actionType)} ${action.changePercent}%`,
              action.beforeValue ?? "-",
              action.afterValue ?? "-",
              `${action.recent30dOrders}单 / ${action.historicalOrders}单`,
              `Orders ${formatSigned(action.outcomeOrdersDelta ?? 0)}，ACOS ${formatSigned(action.outcomeAcosDelta ?? 0)}pp`,
              guardrail.status === "needs_human_review" ? "待审核" : "已归因"
            ];
          })}
        />
      </section>

      <section className="panel wide">
        <PanelTitle icon={ShieldCheck} title="下调比例护栏与审核队列" action="通知管理员" actionMessage="管理员已知情：核心大词/历史强词的下调需要人工审核。" />
        <DataTable
          columns={["ASIN", "Campaign", "AdGroup", "Target", "下调动作", "依据", "风险", "审核", "原因"]}
          rows={guardrailRows}
        />
      </section>

      <section className="panel">
        <PanelTitle icon={MessageSquare} title="运营 / 管理员对话" action="追加问题" actionMessage="已追加问题：如果恢复预算，预计几天内看Orders和TACOS？" />
        <div className="thread">
          <div className="message system">
            <strong>系统</strong>
            <span>B0813TDBPT 核心词预算下调30%后，ASIN订单 36 → 17，触发管理员知情。</span>
          </div>
          <div className="message operator">
            <strong>运营A</strong>
            <span>当时ACOS高于短期目标，按30天数据先降预算，但未注意这是历史强词。</span>
          </div>
          <div className="message admin">
            <strong>管理员</strong>
            <span>恢复到 $72/day，48小时看Orders、TACOS、Top of Search占比，不能直接再次下调。</span>
          </div>
        </div>
      </section>

      <section className="panel full">
        <PanelTitle icon={BookOpen} title="管理员知识库候选" action="批准复用" actionMessage="知识库规则已批准：后续同系列新品生成计划时自动引用。" />
        <DataTable
          columns={["来源动作", "规则标题", "适用范围", "置信度", "下次复用逻辑", "护栏"]}
          rows={reviewKnowledgeItems.map((item) => [
            item.sourceActionId ?? "-",
            item.title,
            item.scope,
            item.confidence === "high" ? "高" : "中",
            item.reuseRule,
            item.nextTimeGuardrail
          ])}
        />
      </section>
    </div>
  );
}

function TrafficArchitecture() {
  return (
    <div className="content-grid">
      <section className="panel wide">
        <PanelTitle icon={GitBranch} title="系列流量架构：3C电源线 / US" action="生成新品架构" actionMessage="新品广告架构已生成：按Auto、Exact、Phrase/Broad、PT、SBV、SD六层搭建。" />
        <div className="architecture-map">
          {["核心词", "1级大词", "2级大词", "1级长尾词", "2级长尾词"].map((tier, index) => (
            <div className="arch-node" key={tier}>
              <span>{index + 1}</span>
              <strong>{tier}</strong>
              <em>{keywordTierRows[index][2]}</em>
            </div>
          ))}
        </div>
      </section>
      <section className="panel">
        <PanelTitle icon={Target} title="复用原则" action="同步到新品" actionMessage="已把当前系列词库、CPC基准和竞品池同步为新品草稿。" />
        <ul className="clean-list">
          <li>核心词只给能承接转化的 ASIN，避免新品硬烧大词。</li>
          <li>长尾词先验证订单，再回流到精准收割组。</li>
          <li>竞品ASIN先看价格、评分、Review差距，再决定截流预算。</li>
          <li>每周把新增词、CPC位置、排名变化写回系列库。</li>
        </ul>
      </section>
      <section className="panel full">
        <PanelTitle icon={Database} title="词层级 x 页面位置 x CPC 基准" action="导出CPC位置库" actionMessage="CPC位置库导出已准备：包含词层级、页面目标、CPC区间和预算基准。" />
        <DataTable
          columns={["词层级", "代表词", "目标页面/位置", "CPC区间", "建议日预算/ASIN", "适用活动", "角色"]}
          rows={keywordTierRows}
        />
      </section>
      <section className="panel full">
        <PanelTitle icon={Gauge} title="产品生命周期预算模型" action="创建预算模板" actionMessage="生命周期预算模板已创建：新品、爬坡、稳定、大促分阶段管理。" />
        <DataTable
          columns={["周期", "目标", "预算占销售额", "CPC/投放策略", "回撤触发"]}
          rows={lifecycleRows}
        />
      </section>
      <section className="panel full">
        <PanelTitle icon={Layers} title="新品广告与流量架构模板" action="生成Campaign命名" actionMessage="Campaign命名已生成：ASIN-广告类型-结构-意图-版本。" />
        <DataTable
          columns={["结构", "目的", "预算占比", "CPC策略", "沉淀动作"]}
          rows={launchRows}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={Store} title="品牌词/活动词使用规则" />
        <ul className="clean-list">
          <li>自品牌词：防守和收割，低TACOS优先保位。</li>
          <li>竞品品牌词：只在价格/评分/规格有优势时打。</li>
          <li>活动词：Prime Day、Back to School、大促周期单独建活动。</li>
          <li>参数词：18AWG、90W、C13 等必须进 Spec Campaign。</li>
        </ul>
      </section>
      <section className="panel">
        <PanelTitle icon={Layers} title="系列竞品与品牌池" action="更新竞品池" actionMessage="竞品池更新任务已创建：后续可接Keepa、卖家精灵、SIF、Sorftime。" />
        <ul className="clean-list">
          <li>Lenovo / Dell / LG / Sony 等品牌按系列维护。</li>
          <li>竞品ASIN记录价格、评分、Review、BSR、Buy Box变化。</li>
          <li>竞品池分为防守、对打、截流、观察四类。</li>
          <li>新品复用时自动带出同系列竞品清单。</li>
        </ul>
      </section>
    </div>
  );
}

function reviewActionLabel(actionType: AdActionEvidence["actionType"]) {
  const labels: Record<AdActionEvidence["actionType"], string> = {
    increase_budget: "提高预算",
    decrease_budget: "降低预算",
    increase_bid: "提高竞价",
    decrease_bid: "降低竞价",
    pause_campaign: "暂停活动",
    add_keyword: "加词",
    add_negative: "否词/否ASIN",
    placement_adjustment: "广告位调整"
  };
  return labels[actionType];
}

function formatSigned(value: number) {
  return `${value > 0 ? "+" : ""}${value}`;
}

function Bi() {
  return (
    <div className="content-grid">
      <section className="panel full">
        <PanelTitle icon={BarChart3} title="BI经营驾驶舱" action="刷新数据" actionMessage="BI数据已刷新：按当前店铺、站点、ASIN和周期重算。" />
        <div className="filter-bar">
          {["US主店", "全部站点", "全部系列", "近30天", "按ASIN"].map((filter) => (
            <button key={filter}>{filter}</button>
          ))}
        </div>
      </section>
      <KpiRow />
      <section className="panel wide">
        <PanelTitle icon={BarChart3} title="销售 / 花费 / ACOS趋势" action="切换周/月" actionMessage="已切换趋势粒度：支持日、周、月环比。" />
        <div className="combo-chart">
          {[62, 74, 69, 82, 88, 94, 76, 91].map((height, index) => (
            <div className="bar-col" key={`${height}-${index}`}>
              <div style={{ height: `${height}%` }} />
              <span>06-{String(index + 1).padStart(2, "0")}</span>
            </div>
          ))}
          <svg viewBox="0 0 800 220" className="trend-line" aria-label="ACOS趋势线">
            <polyline points="18,70 126,86 234,80 342,104 450,118 558,132 666,120 782,146" />
          </svg>
        </div>
      </section>
      <section className="panel">
        <PanelTitle icon={Target} title="目标达成" />
        <div className="goal-stack">
          <Goal label="长期TACOS 12%" value={86} text="当前 13.9%" />
          <Goal label="长期ACOS 25%" value={78} text="当前 31.8%" />
          <Goal label="订单增长 +100%" value={41} text="当前 +28%" />
          <Goal label="API预算使用" value={18} text="$18 / $500" />
        </div>
      </section>
      <section className="panel full">
        <PanelTitle icon={Store} title="ASIN经营排行与动作入口" action="生成复盘报告" actionMessage="复盘报告已生成草稿：包含ASIN、词层级、预算和行动执行前后对比。" />
        <DataTable
          columns={["ASIN", "产品", "站点", "销售额", "订单", "广告花费", "ACOS", "TACOS", "风险", "下一步动作"]}
          rows={asinPerformanceRows}
        />
      </section>
      <section className="panel wide">
        <PanelTitle icon={GitBranch} title="词层级贡献与CPC位置" action="写回系列库" actionMessage="词层级表现已写回系列流量架构库。" />
        <DataTable
          columns={["流量层级", "销售额", "销售占比", "ACOS", "平均CPC", "当前位置", "策略"]}
          rows={trafficContributionRows}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={ShieldCheck} title="异常预警" action="生成处理清单" actionMessage="异常处理清单已同步到行动计划草稿。" />
        <DataTable
          columns={["等级", "对象", "异常", "建议"]}
          rows={alertsRows}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={Gauge} title="预算节奏" />
        <DataTable
          columns={["店铺", "日预算上限", "今日花费", "消耗进度", "状态", "动作"]}
          rows={budgetPacingRows}
        />
      </section>
      <section className="panel">
        <PanelTitle icon={Activity} title="行动复盘" />
        <DataTable
          columns={["动作", "建议数", "执行状态", "影响", "结论"]}
          rows={actionReviewRows}
        />
      </section>
      <section className="panel full">
        <PanelTitle icon={Layers} title="站点 / 系列 / 竞品复盘" action="进入竞品库" actionMessage="已跳转竞品库：可继续维护系列竞品ASIN和品牌池。" navigateTo="competitors" />
        <div className="review-grid">
          {[
            ["US", "3C电源线", "Lenovo/Dell/LG/Sony", "核心词保位，长尾拉精准"],
            ["EU", "适配器系列", "HP/Acer/Asus", "补CPC库和本地语言词"],
            ["JP", "TV电源系列", "Sony/Panasonic", "低CPC竞品ASIN测试"]
          ].map(([site, series, rivals, action]) => (
            <div className="review-card" key={site}>
              <strong>{site} · {series}</strong>
              <span>竞品：{rivals}</span>
              <em>{action}</em>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Goal({ label, value, text }: { label: string; value: number; text: string }) {
  return (
    <div className="goal">
      <div>
        <strong>{label}</strong>
        <span>{text}</span>
      </div>
      <div className="progress"><span style={{ width: `${value}%` }} /></div>
    </div>
  );
}

function Library() {
  return (
    <section className="panel full">
      <PanelTitle icon={Database} title="系列关键词库 / CPC竞价库" action="导入词库" actionMessage="词库导入入口已打开：支持CSV/Excel和后续MCP同步。" />
      <DataTable
        columns={["系列", "站点", "关键词", "意图层", "建议匹配", "目标CPC", "排名进度"]}
        rows={[
          ["3C电源线", "US", "90w usb c charger", "规格参数", "Exact/Phrase", "$2.60", "自然位 18 → 11"],
          ["3C电源线", "US", "lenovo thinkpad charger", "品牌/对标", "Exact", "$3.20", "广告位 Top 12%"],
          ["Sony TV电源", "JP", "sony bravia power adapter", "兼容/场景", "Phrase", "¥68", "待追踪"]
        ]}
      />
    </section>
  );
}

function Competitors() {
  return (
    <section className="panel full">
      <PanelTitle icon={Layers} title="竞品ASIN与品牌库" action="同步Keepa" actionMessage="Keepa同步任务已创建为草稿，需配置API Key后启用。" />
      <DataTable
        columns={["站点", "竞品ASIN", "品牌", "价格", "评分", "来源", "建议动作"]}
        rows={[
          ["US", "B0COMPETITOR1", "Lenovo", "$18.99", "4.4", "ABA/Keepa", "PT截流"],
          ["US", "B0COMPETITOR2", "Dell", "$22.49", "4.2", "搜索词", "低价对打"],
          ["JP", "B0COMPETITOR3", "Sony", "¥2480", "4.1", "MCP预留", "待验证"]
        ]}
      />
    </section>
  );
}

function Models() {
  const act = useAction();
  const emptyDraft: LlmProvider = {
    id: "",
    name: "",
    type: "OpenAI兼容",
    baseUrl: "",
    model: "",
    status: "未填Key",
    quota: "$100/月",
    apiKey: ""
  };
  const [providers, setProviders] = useState<LlmProvider[]>(initialLlmProviders);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LlmProvider>(emptyDraft);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet<{ providers: Array<{
      id: string;
      name: string;
      providerType: string;
      baseUrl: string;
      modelName: string;
      hasApiKey: boolean;
      enabled: boolean;
    }> }>("/api/llm/providers")
      .then((result) => {
        if (!alive) return;
        setProviders(result.providers.map((provider) => ({
          id: provider.id,
          name: provider.name,
          type: providerTypeToUi(provider.providerType),
          baseUrl: provider.baseUrl,
          model: provider.modelName,
          status: provider.hasApiKey ? "已填Key" : "未填Key",
          quota: provider.enabled ? "已启用" : "未启用",
          hasApiKey: provider.hasApiKey,
          enabled: provider.enabled
        })));
      })
      .catch(() => {
        if (alive) act("后端模型配置暂不可用，当前显示本地预设。");
      });
    return () => {
      alive = false;
    };
  }, [act]);

  function startCreate() {
    setEditingId("new");
    setDraft({ ...emptyDraft, id: `provider-${Date.now()}` });
  }

  function startEdit(provider: LlmProvider) {
    setEditingId(provider.id);
    setDraft({ ...provider, apiKey: "" });
  }

  async function saveProvider() {
    if (!draft.name.trim() || !draft.baseUrl.trim() || !draft.model.trim()) {
      act("供应商、Base URL 和模型名不能为空。");
      return;
    }
    setLoading(true);
    try {
      const result = await apiPost<{ provider: {
        id: string;
        name: string;
        providerType: string;
        baseUrl: string;
        modelName: string;
        hasApiKey: boolean;
        enabled: boolean;
      } }>("/api/llm/providers", {
        id: draft.id,
        name: draft.name,
        providerType: uiTypeToProvider(draft.type),
        baseUrl: draft.baseUrl,
        modelName: draft.model,
        apiKey: draft.apiKey,
        enabled: true
      });
      const normalized = {
        id: result.provider.id,
        name: result.provider.name,
        type: providerTypeToUi(result.provider.providerType),
        baseUrl: result.provider.baseUrl,
        model: result.provider.modelName,
        status: result.provider.hasApiKey ? "已填Key" : "未填Key",
        quota: result.provider.enabled ? "已启用" : "未启用",
        hasApiKey: result.provider.hasApiKey,
        enabled: result.provider.enabled
      };
      setProviders((current) => {
        const exists = current.some((provider) => provider.id === normalized.id);
        return exists ? current.map((provider) => provider.id === normalized.id ? normalized : provider) : [normalized, ...current];
      });
      setEditingId(null);
      setDraft(emptyDraft);
      act("LLM供应商配置已保存到后端，可以测试连接。");
    } catch (error) {
      act(error instanceof Error ? error.message : "LLM供应商保存失败。");
    } finally {
      setLoading(false);
    }
  }

  async function testProvider(provider: LlmProvider) {
    setLoading(true);
    try {
      const result = await apiPost<{ ok: boolean; latencyMs: number; output?: string; error?: string }>(
        `/api/llm/providers/${provider.id}/test`,
        { prompt: "请回复：LLM连接成功，可以开始广告分析。" }
      );
      act(result.ok ? `连接成功：${result.output || "模型已响应"}（${result.latencyMs}ms）` : `连接失败：${result.error}`);
    } catch (error) {
      act(error instanceof Error ? error.message : "LLM连接测试失败。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="content-grid">
      <section className="panel full">
        <PanelTitle icon={Bot} title="LLM API配置" action="新增供应商" onAction={startCreate} />
        {editingId && (
          <div className="model-form">
            <label>供应商<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="例如 DeepSeek / OpenAI中转" /></label>
            <label>类型
              <select value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value })}>
                <option>OpenAI兼容</option>
                <option>Proxy</option>
                <option>Gemini</option>
                <option>Anthropic</option>
                <option>xAI</option>
                <option>自定义</option>
              </select>
            </label>
            <label>Base URL<input value={draft.baseUrl} onChange={(event) => setDraft({ ...draft, baseUrl: event.target.value })} placeholder="https://api.example.com/v1" /></label>
            <label>模型名<input value={draft.model} onChange={(event) => setDraft({ ...draft, model: event.target.value })} placeholder="model-name" /></label>
            <label>API Key<input type="password" value={draft.apiKey ?? ""} onChange={(event) => setDraft({ ...draft, apiKey: event.target.value })} placeholder="只保存加密状态，不在前端明文展示" /></label>
            <label>月额度<input value={draft.quota} onChange={(event) => setDraft({ ...draft, quota: event.target.value })} placeholder="$100/月" /></label>
            <div className="form-actions">
              <button className="primary" onClick={saveProvider} disabled={loading}>{loading ? "保存中" : "保存配置"}</button>
              <button onClick={() => {
                setEditingId(null);
                setDraft(emptyDraft);
              }} disabled={loading}>取消</button>
            </div>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {["供应商", "类型", "Base URL", "模型", "状态", "额度", "操作"].map((column) => <th key={column}>{column}</th>)}
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => (
                <tr key={provider.id}>
                  <td>{provider.name}</td>
                  <td>{provider.type}</td>
                  <td>{provider.baseUrl}</td>
                  <td>{provider.model}</td>
                  <td>{provider.status}</td>
                  <td>{provider.quota}</td>
                  <td>
                    <div className="row-actions">
                      <button onClick={() => startEdit(provider)} disabled={loading}>编辑</button>
                      <button onClick={() => testProvider(provider)} disabled={loading || !provider.hasApiKey}>测试连接</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function uiTypeToProvider(type: string) {
  const map: Record<string, string> = {
    "OpenAI兼容": "openai_compatible",
    Proxy: "proxy",
    Gemini: "gemini",
    Anthropic: "anthropic",
    xAI: "xai",
    自定义: "custom"
  };
  return map[type] ?? "openai_compatible";
}

function providerTypeToUi(type: string) {
  const map: Record<string, string> = {
    openai_compatible: "OpenAI兼容",
    proxy: "Proxy",
    gemini: "Gemini",
    anthropic: "Anthropic",
    xai: "xAI",
    custom: "自定义",
    openai: "OpenAI兼容"
  };
  return map[type] ?? type;
}

function UsersPage() {
  const act = useAction();
  const emptyUser: ManagedUser = {
    id: "",
    username: "",
    displayName: "",
    role: "operator",
    status: "active",
    maxDevices: 1,
    activeDeviceCount: 0,
    permissions: ["upload", "analysis", "plans", "bi", "library"],
    monthlyTokenLimit: 800000,
    monthlyCostLimitUsd: 80,
    usedTokens: 0,
    usedCostUsd: 0,
    password: ""
  };
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [devices, setDevices] = useState<ManagedDevice[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ManagedUser>(emptyUser);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const result = await apiGet<{ users: ManagedUser[] }>("/api/users");
      setUsers(result.users);
      const deviceResult = await apiGet<{ devices: ManagedDevice[] }>("/api/user-devices");
      setDevices(deviceResult.devices);
    } catch (error) {
      act(error instanceof Error ? error.message : "用户权限加载失败。");
    } finally {
      setLoading(false);
    }
  }

  async function releaseDevice(deviceId: string) {
    setLoading(true);
    try {
      await apiPost(`/api/user-devices/${deviceId}/release`);
      act("设备已释放，该员工可在新电脑重新登录。");
      await loadUsers();
    } catch (error) {
      act(error instanceof Error ? error.message : "释放设备失败。");
    } finally {
      setLoading(false);
    }
  }

  function startCreate() {
    setEditingId("new");
    setDraft({ ...emptyUser, id: `user-${Date.now()}` });
  }

  function startEdit(user: ManagedUser) {
    setEditingId(user.id);
    setDraft({ ...user, password: "" });
  }

  async function saveManagedUser() {
    if (!draft.username.trim() || !draft.displayName.trim()) {
      act("账号和姓名不能为空。");
      return;
    }
    if (editingId === "new" && !draft.password?.trim()) {
      act("新增员工必须设置初始密码。");
      return;
    }
    setLoading(true);
    try {
      await apiPost("/api/users", {
        id: editingId === "new" ? undefined : draft.id,
        username: draft.username,
        displayName: draft.displayName,
        password: draft.password?.trim() || undefined,
        role: draft.role,
        status: draft.status,
        maxDevices: draft.maxDevices,
        permissions: draft.permissions,
        monthlyTokenLimit: draft.monthlyTokenLimit,
        monthlyCostLimitUsd: draft.monthlyCostLimitUsd
      });
      act("员工账号权限已保存。");
      setEditingId(null);
      setDraft(emptyUser);
      await loadUsers();
    } catch (error) {
      act(error instanceof Error ? error.message : "员工账号保存失败。");
    } finally {
      setLoading(false);
    }
  }

  function togglePermission(permission: string) {
    const next = draft.permissions.includes(permission)
      ? draft.permissions.filter((item) => item !== permission)
      : [...draft.permissions, permission];
    setDraft({ ...draft, permissions: next });
  }

  return (
    <div className="content-grid">
      <section className="panel full">
        <PanelTitle icon={Users} title="用户权限与设备控制" action="新增运营" onAction={startCreate} />
        {editingId && (
          <div className="user-form">
            <label>账号<input value={draft.username} onChange={(event) => setDraft({ ...draft, username: event.target.value })} placeholder="operator-a" /></label>
            <label>姓名<input value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} placeholder="运营姓名" /></label>
            <label>密码<input type="password" value={draft.password ?? ""} onChange={(event) => setDraft({ ...draft, password: event.target.value })} placeholder={editingId === "new" ? "初始密码" : "留空则不修改"} /></label>
            <label>角色
              <select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as ManagedUser["role"] })}>
                <option value="operator">运营</option>
                <option value="auditor">审计</option>
                <option value="admin">管理员</option>
              </select>
            </label>
            <label>账号状态
              <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as ManagedUser["status"] })}>
                <option value="active">启用</option>
                <option value="disabled">停用</option>
              </select>
            </label>
            <label>允许电脑数<input type="number" min="1" max="20" value={draft.maxDevices} onChange={(event) => setDraft({ ...draft, maxDevices: Number(event.target.value) })} /></label>
            <label>月Token额度<input type="number" min="0" value={draft.monthlyTokenLimit} onChange={(event) => setDraft({ ...draft, monthlyTokenLimit: Number(event.target.value) })} /></label>
            <label>月金额额度($)<input type="number" min="0" value={draft.monthlyCostLimitUsd} onChange={(event) => setDraft({ ...draft, monthlyCostLimitUsd: Number(event.target.value) })} /></label>
            <div className="permission-grid">
              {[
                ["upload", "报表上传"],
                ["analysis", "分析任务"],
                ["plans", "行动计划"],
                ["review", "复盘审计"],
                ["traffic", "流量架构"],
                ["bi", "BI看板"],
                ["library", "词库/CPC库"],
                ["competitors", "竞品库"],
                ["models", "模型API"],
                ["connectors", "外部接口"],
                ["users", "用户权限"]
              ].map(([permission, label]) => (
                <label className="check-row" key={permission}>
                  <input type="checkbox" checked={draft.permissions.includes(permission) || draft.permissions.includes("all")} onChange={() => togglePermission(permission)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="form-actions">
              <button className="primary" onClick={saveManagedUser} disabled={loading}>{loading ? "保存中" : "保存账号"}</button>
              <button onClick={() => {
                setEditingId(null);
                setDraft(emptyUser);
              }} disabled={loading}>取消</button>
            </div>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {["账号", "姓名", "角色", "设备", "权限", "API额度", "已用", "状态", "操作"].map((column) => <th key={column}>{column}</th>)}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.displayName}</td>
                  <td>{roleLabel(user.role)}</td>
                  <td>{user.activeDeviceCount}/{user.maxDevices}</td>
                  <td>{user.permissions.includes("all") ? "全部" : user.permissions.join(", ")}</td>
                  <td>${user.monthlyCostLimitUsd} / {user.monthlyTokenLimit.toLocaleString()} tokens</td>
                  <td>${user.usedCostUsd} / {user.usedTokens.toLocaleString()}</td>
                  <td>{user.status === "active" ? "启用" : "停用"}</td>
                  <td><button onClick={() => startEdit(user)} disabled={loading}>编辑</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel full">
        <PanelTitle icon={ShieldCheck} title="员工电脑绑定明细" action="刷新设备" onAction={loadUsers} />
        <DataTable
          columns={["账号", "员工", "电脑", "状态", "首次登录", "最近使用", "操作"]}
          rows={devices.map((device) => [
            device.username,
            device.displayName,
            device.deviceLabel,
            device.status === "active" ? "启用" : "已释放",
            device.firstSeenAt,
            device.lastSeenAt,
            device.status === "active" ? "可释放" : "-"
          ])}
        />
        <div className="device-actions">
          {devices.filter((device) => device.status === "active").map((device) => (
            <button key={device.id} onClick={() => releaseDevice(device.id)} disabled={loading}>
              释放 {device.username} · {device.deviceLabel}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function roleLabel(role: ManagedUser["role"]) {
  if (role === "admin") return "管理员";
  if (role === "auditor") return "审计";
  return "运营";
}

function Connectors() {
  return (
    <section className="panel full">
      <PanelTitle icon={Plug} title="ERP / MCP / Amazon / Keepa / 协作接口预留" action="测试连接" actionMessage="连接测试入口已触发：未配置密钥的接口保持预留状态。" />
      <DataTable columns={["类型", "服务", "状态", "能力"]} rows={connectorRows} />
      <div className="notice">
        <KeyRound size={18} />
        <span>所有外部密钥只写入 Cloudflare Secrets 或加密凭证字段，不进入前端包和Git提交。</span>
      </div>
    </section>
  );
}

function TaskList() {
  return (
    <div className="task-list">
      {actionPlans.slice(0, 3).map((plan) => (
        <div className="task" key={plan.target}>
          <strong>{plan.priority} · {plan.dimension}</strong>
          <span>{plan.asin} / {plan.target}</span>
          <em>{plan.due}</em>
        </div>
      ))}
    </div>
  );
}

function StatusList() {
  return (
    <div className="status-list">
      {["D1 数据库已配置", "R2 文件存储已绑定", "Queues 任务已预留", "Amazon Ads API 自动执行关闭"].map((item) => (
        <div key={item}><span />{item}</div>
      ))}
    </div>
  );
}

function PanelTitle({
  icon: Icon,
  title,
  action,
  actionMessage,
  navigateTo,
  onAction
}: {
  icon: React.ElementType;
  title: string;
  action?: string;
  actionMessage?: string;
  navigateTo?: Section;
  onAction?: () => void;
}) {
  const act = useAction();
  return (
    <div className="panel-title">
      <div>
        <Icon size={19} />
        <h2>{title}</h2>
      </div>
      {action && <button onClick={onAction ?? (() => act(actionMessage ?? `${action} 已触发。`, navigateTo))}>{action}</button>}
    </div>
  );
}

function DataTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => <th key={column}>{column}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);

function exportCsv() {
  const header = ["优先级", "维度", "ASIN", "Campaign", "AdGroup", "Target", "当前数据", "建议动作", "负责人", "执行时间"];
  const rows = actionPlans.map((plan) => [
    plan.priority,
    plan.dimension,
    plan.asin,
    plan.campaign,
    plan.adGroup,
    plan.target,
    plan.current,
    plan.action,
    plan.owner,
    plan.due
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${cell.split('"').join('""')}"`).join(","))
    .join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "amazon-ad-action-plan.csv";
  link.click();
  URL.revokeObjectURL(url);
}
